import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import { resolveSceneById } from '../tools/scene-addressing';
import { isLayeredGraphicScene } from './layeredGraphic';
import { Tool, type ToolConstructorArgs } from '../tools/Tool';
import type { ConversationMessage, EnhancedToolResult, ToolDefinition } from '../types';
import { getActiveVideoRecipe } from '../skills/loadedSkills';
import { techniquePolicyFor, type TechniquePolicy } from './skills/recipeTechniquePolicies';

import { MOTION_GRAPHICS_SCENE_DESIGN_ID } from 'js/src/agent/tools/motion-graphics/motionGraphicsSceneDesign';
import type { RootState } from 'js/src/libs/RootState';
import type { HtmlAsset } from 'js/src/luminary/html-asset-operations';

/** The scene comes along because it, not the composition, owns the graphic's playback duration. */
export type ResolveAssetResult = { asset: HtmlAsset; scene: Scene } | { error: EnhancedToolResult };

export const designTargetSchemaFields = {
    scene_id: z
        .string()
        .describe('The motion graphics scene whose graphic this call acts on.')
        .optional(),
};

export function resolveDesignTarget(
    state: RootState,
    luminary: Luminary,
    sceneId: string | undefined,
): ResolveAssetResult {
    if (!sceneId) {
        return {
            error: {
                result:
                    'Error: scene_id is required — name the motion graphics scene this call ' +
                    'acts on, exactly as it appears in the current video state.',
                isSuccess: false,
            },
        };
    }
    const resolved = resolveSceneById(luminary, state, sceneId);
    if ('error' in resolved) {
        return { error: { result: `Error: ${resolved.error}`, isSuccess: false } };
    }
    const { scene } = resolved;
    const asset = scene.getAssetsByType(VideoData.HTML_ASSET_TYPE)[0] as HtmlAsset | undefined;
    if (!asset) {
        return {
            error: {
                result:
                    `Error: scene ${sceneId} has no graphic to design. Lay one over it with a ` +
                    `\`graphic\` ${TOOL_NAMES.runSubagent}, add a scene that is all graphic with ` +
                    `${TOOL_NAMES.addScene} using scene_design_id "${MOTION_GRAPHICS_SCENE_DESIGN_ID}", ` +
                    'or use the regular scene tools to change this scene.',
                isSuccess: false,
            },
        };
    }
    return { asset, scene };
}

export interface DesignToolArgs extends ToolConstructorArgs {
    /**
     * The messages the next request will send — after compaction and truncation, not the raw
     * history — read for the recipe the video is built under and the skills still in context.
     */
    getConversationMessages?: () => ConversationMessage[];
}

export abstract class DesignTool<
    TSchema extends z.ZodType = z.ZodType,
    TArgs extends DesignToolArgs = DesignToolArgs,
> extends Tool<TSchema, TArgs> {
    protected readonly getConversationMessages?: () => ConversationMessage[];

    public constructor(args: TArgs) {
        super(args);
        this.getConversationMessages = args.getConversationMessages;
    }

    /** The policy of the recipe the video is built under, if it sets one. */
    protected recipeTechniquePolicy(): TechniquePolicy | undefined {
        return techniquePolicyFor(
            getActiveVideoRecipe(this.getConversationMessages?.() ?? [])?.name,
        );
    }

    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        const required = parameters.required ?? [];
        return {
            ...parameters,
            required: required.includes('scene_id') ? required : [...required, 'scene_id'],
        };
    }

    protected resolveTarget(sceneId: string | undefined): ResolveAssetResult {
        return resolveDesignTarget(this.getState(), this.luminary, sceneId);
    }

    /** The graphic to change — refused when it is layered over a scene, which its designer owns. */
    protected resolveAuthoringTarget(sceneId: string | undefined): ResolveAssetResult {
        const resolved = this.resolveTarget(sceneId);
        if ('error' in resolved || !isLayeredGraphicScene(resolved.scene)) {
            return resolved;
        }
        return {
            error: {
                result:
                    `Error: the graphic on scene ${sceneId} is layered over the scene and has its own ` +
                    `designer. Change it by starting a \`graphic\` ${TOOL_NAMES.runSubagent} on the ` +
                    'scene, saying what to change.',
                isSuccess: false,
            },
        };
    }
}
